import React from 'react'
import * as yup from 'yup'
import { Grid, Pagination } from '@softwaredev_cegeka/stix-core'
import {
    Fieldset,
    FormGroup,
    Input,
    Textarea,
} from '@softwaredev_cegeka/stix-form'

import BaseForm from 'src/components/Forms/BaseForm'
import { authenticatedUserStore, notifierStore } from 'src/stores'
import { AuthApi } from 'src/api/authApi'
import { ServiceActionsTable } from 'src/components/EntityTable'
import { Button } from '@softwaredev_cegeka/stix-core/dist/components'
import Icon from '@softwaredev_cegeka/stix-core/dist/components/Icon'
import { Link } from 'react-router-dom'

import './serviceForm.scss'

class ServiceForm extends BaseForm {
    pages = [
        {
            renderMethod: this.renderForm,
            validationSchema: yup.object().shape({
                serviceName: yup
                    .string()
                    .matches(/^[a-zA-Z0-9 ]+$/, {
                        message: 'Please insert alphanumeric characters only',
                        excludeEmptyString: true,
                    })
                    .max(255, 'Please insert less than 255 characters')
                    .required('Please insert the name of the service'),
                namespace: yup
                    .string()
                    .matches(/^[a-zA-Z0-9]+$/, {
                        message: 'Please insert alphanumeric characters only',
                        excludeEmptyString: true,
                    })
                    .max(255, 'Please insert less than 255 characters')
                    .required('Please insert the namespace'),
                description: yup
                    .string()
                    .matches(/^[a-zA-Z0-9 ]+$/, {
                        message: 'Please insert alphanumeric characters only',
                        excludeEmptyString: true,
                    })
                    .required('Please add a short description'),
            }),
        },
    ]

    initialValues = {
        serviceName: '',
        namespace: '',
        description: '',
    }

    authClient = new AuthApi(authenticatedUserStore.authToken)

    constructor(props, context) {
        super(props, context)
        this.state.values = {
            ...this.initialValues,
        }
        notifierStore.setShowMessage(false)
        this.state = {
            ...this.state,
            serviceActions: [...Array(10)].map(item => ({})),
            page: 1,
            per_page: 10,
            loading: false,
            totalPages: 1,
            serviceCreated: false,
            params: {},
        }
        if (props.match.params.id) {
            this.authClient
                .fetchService(props.match.params.id)
                .then(response => {
                    this.initialValues = {
                        serviceName: response.data.name,
                        namespace: response.data.namespace,
                        description: response.data.short_description,
                    }
                    this.setState({
                        values: { ...this.initialValues },
                        initialValues: { ...this.initialValues },
                    })
                })
            this.fetchAndStoreServiceActions()
        }
    }

    componentWillUnmount() {
        this.authClient.cancelRequest()
    }

    setParams = params => {
        this.setState(
            {
                params: params,
            },
            () => this.fetchAndStoreServiceActions()
        )
    }

    fetchAndStoreServiceActions = (
        page = this.state.page,
        per_page = this.state.per_page
    ) => {
        this.state = {
            ...this.state,
            loading: true,
        }
        this.authClient
            .fetchServiceActions(this.props.match.params.id, page, per_page, {
                sort: '+category',
                ...this.state.params,
            })
            .then(response => {
                this.setState({
                    serviceActions: response?.data?.results,
                    totalPages: response?.data?._page.total_pages,
                    loading: false,
                })
            })
    }

    deleteServiceAction = serviceActionId => {
        this.authClient
            .deleteServiceAction(this.props.match.params.id, serviceActionId)
            .then(response => {
                if (response.status === 200 || response.status === 204) {
                    this.fetchAndStoreServiceActions()
                    notifierStore.setToastInfo(
                        'Service action successfully deleted',
                        true
                    )
                } else {
                    notifierStore.setToastInfo(
                        'Service action could not be deleted',
                        false
                    )
                }
            })
            .catch(err => {
                notifierStore.setToastInfo(
                    err.response?.data?.error ||
                        'Service action could not be deleted',
                    false
                )
            })
            .finally(notifierStore.setShowMessage(true))
    }

    handlePageChange = page => {
        this.setState(
            {
                page: page,
            },
            () => this.fetchAndStoreServiceActions()
        )
    }

    getSubmitUrl() {
        if (this.props.match.params.id) {
            return `/api/auth/services/${this.props.match.params.id}`
        }
        return '/api/auth/services'
    }

    getSubmitMethod() {
        if (this.props.match.params.id) {
            return 'PUT'
        }
        return 'POST'
    }

    generatePayload = () => {
        const values = this.state.values
        if (this.props.match.params.id) {
            let changedData = {}
            const initialValues = this.state.initialValues
            Object.keys(values).forEach(key => {
                if (values[key] !== initialValues[key]) {
                    changedData[key] = values[key]
                }
            })
            if (Object.keys(changedData).length === 0) {
                this.setState(
                    {
                        toastMessage: 'No changes were made to the form',
                        didSucceed: false,
                    },
                    () => {
                        notifierStore.setShowMessage(true)
                    }
                )
                return null
            }
        }
        return {
            name: values.serviceName,
            namespace: values.namespace,
            short_description: values.description,
        }
    }

    getSuccessMessage() {
        if (this.props.match.params.id) {
            return 'Service information successfully updated'
        }
        this.setState({
            serviceCreated: true,
        })
        return 'Service successfully created'
    }

    getFailureMessage() {
        if (this.props.match.params.id) {
            return 'Service information could not be updated'
        }
        return 'Service could not be created'
    }

    // overwrite this in order to render the buttons separately
    renderFormActions(pageNumber) {
        return null
    }

    renderForm() {
        return (
            <>
                <Fieldset title="General info">
                    <Grid.Row>
                        <Grid.Col>
                            <FormGroup>
                                <Input
                                    {...this.getCommonInputProps('serviceName')}
                                    label="Service Name"
                                    required
                                />
                            </FormGroup>
                        </Grid.Col>
                        <Grid.Col>
                            <FormGroup>
                                <Input
                                    {...this.getCommonInputProps('namespace')}
                                    label="Namespace"
                                    required
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Col lg={6}>
                            <FormGroup>
                                <Textarea
                                    {...this.getCommonInputProps('description')}
                                    label="Short description"
                                    required
                                />
                            </FormGroup>
                        </Grid.Col>
                    </Grid.Row>
                </Fieldset>
                <Grid.Row>
                    <Grid.Col>
                        <Button
                            className={'mb-3'}
                            size={'sm'}
                            key="submit"
                            variant="secondary"
                            onClick={this.handleSubmit}
                        >
                            Submit
                        </Button>
                    </Grid.Col>
                </Grid.Row>
                {(this.props.match.params.id || this.state.serviceCreated) && (
                    <Fieldset title="Service actions">
                        {this.props.match.params.id && (
                            <div className="data-table-container form-data-table">
                                <ServiceActionsTable
                                    {...this.props}
                                    data={this.state.serviceActions}
                                    loading={this.state.loading}
                                    params={this.state.params}
                                    setParams={this.setParams}
                                    deleteAction={this.deleteServiceAction}
                                    canDelete={true}
                                    canEdit={true}
                                    editPage={'/service-form/'}
                                />

                                {this.state.totalPages >= 2 && (
                                    <div className="pagination-wrapper">
                                        <Pagination
                                            lastPage={this.state.totalPages}
                                            currentPage={this.state.page}
                                            onClick={
                                                this.state.loading
                                                    ? () => {}
                                                    : this.handlePageChange
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        <div className={'service-action-add-btn'}>
                            <Link
                                to={`/service-form/${
                                    this.props?.match.params.id ||
                                    this.state.createdEntityId
                                }/service-action`}
                            >
                                <Icon icon="add" />
                                <span>Add service action</span>
                            </Link>
                        </div>
                    </Fieldset>
                )}
            </>
        )
    }
}

export default ServiceForm
